import React, { useState } from "react";
import { FiMail, FiPhone, FiMapPin, FiSend } from "react-icons/fi";
import Reveal from "../ui/Reveal";
import SectionHeading from "../ui/SectionHeading";
import SocialIcon from "../ui/SocialIcon";
import { SITE, SOCIALS, CONTACT_ENDPOINT } from "../../data/portfolio";

const initial = { name: "", email: "", message: "" };

const ContactForm = () => {
  const [formData, setFormData] = useState(initial);
  const [status, setStatus] = useState(null);
  const [statusColor, setStatusColor] = useState("text-ink-muted");
  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setFormData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setStatusColor("text-ink-muted");
    // setStatus("Sending…");

    const slow = setTimeout(
      () => setStatus("Hold on — the server is waking up…"),
      5000
    );

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      clearTimeout(slow);
      if (!res.ok) throw new Error("bad response");
      setStatusColor("text-emerald-400");
      setStatus("Message sent — I'll get back to you soon.");
      setFormData(initial);
      setTimeout(() => setStatus(null), 6000);
    } catch (err) {
      clearTimeout(slow);
      setStatusColor("text-red-400");
      setStatus("Something went wrong. Try again or email me directly.");
      setTimeout(() => setStatus(null), 6000);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-line bg-white/[0.03] px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink-faint focus:border-accent/60 focus:ring-2 focus:ring-accent/20";

  return (
    <section id="contact" className="section">
      <div className="container-page flex flex-col gap-14">
        <SectionHeading
          kicker="Contact"
          title="Let's build something"
          subtitle="Have a project, a role or just a question? My inbox is open."
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(280px,0.8fr)] lg:items-center">
          <Reveal className="card-surface min-w-0 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-sm font-medium text-ink-muted">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className={inputCls}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-medium text-ink-muted">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@company.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="text-sm font-medium text-ink-muted">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me a little about what you're working on…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="btn btn-primary w-full disabled:opacity-60"
              >
                <FiSend /> {sending ? "Sending…" : "Send message"}
              </button>
              {status && (
                <p className={`text-center text-sm ${statusColor}`}>{status}</p>
              )}
            </form>
          </Reveal>

          <Reveal delay={80} className="flex flex-col gap-4">
            <div className="card-surface flex flex-col gap-4 p-6">
              <a
                href={`mailto:${SITE.email}`}
                className="flex items-center gap-3 text-sm text-ink-muted no-underline transition-colors hover:text-accent-soft"
              >
                <span className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-white/5 text-accent-soft">
                  <FiMail />
                </span>
                <span className="break-all">{SITE.email}</span>
              </a>
              <div className="flex items-center gap-3 text-sm text-ink-muted">
                <span className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-white/5 text-accent-soft">
                  <FiPhone />
                </span>
                {SITE.phone}
              </div>
              <div className="flex items-center gap-3 text-sm text-ink-muted">
                <span className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-white/5 text-accent-soft">
                  <FiMapPin />
                </span>
                {SITE.location}
              </div>
            </div>

            <div className="card-surface p-6">
              <p className="text-sm font-medium text-ink">Find me online</p>
              <div className="mt-4 flex gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-white/5 text-lg text-ink-muted transition-all hover:-translate-y-0.5 hover:border-accent/50 hover:text-accent-soft"
                  >
                    <SocialIcon name={s.icon} />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
